import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { FileText, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react'
import { useTenantBranding } from '../../../contexts/TenantContext'

const API_BASE = import.meta.env.VITE_PROJECTS_API_URL || '/api/projects'

export default function ClientApproval() {
  const { slug, code } = useParams()
  const brand = useTenantBranding()
  const [ticket, setTicket] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [approverName, setApproverName] = useState('')
  // Brought up to the proposal page's shape, 2026-08-23. This form asked for a name and a signature
  // and nothing else — no email to put the signer on the directory, no title to store, and no
  // record of what they were agreeing to on the document a T&M invoice is built from.
  const [approverTitle, setApproverTitle] = useState('')
  const [approverEmail, setApproverEmail] = useState('')
  const [consent, setConsent] = useState(null)      // {consent_key, consent_version, locale, text}
  const [agreed, setAgreed] = useState(false)
  const [rejectionReason, setRejectionReason] = useState('')
  const [isRejecting, setIsRejecting] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState(null)
  const canvasRef = useRef(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [hasSigned, setHasSigned] = useState(false)

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/client/approve/${slug}/${code}`)
        if (!res.ok) {
          const data = await res.json().catch(() => ({}))
          throw new Error(data.detail || 'Invalid or expired approval link')
        }
        const data = await res.json()
        setTicket(data)
      } catch (e) {
        setError(e.message)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [slug, code])

  // Signature canvas setup
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const rect = canvas.getBoundingClientRect()
    canvas.width = rect.width * 2
    canvas.height = rect.height * 2
    ctx.scale(2, 2)
    ctx.strokeStyle = '#1a1a1a'
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
  }, [ticket])

  // The ticket document itself, resolved by the same approval token this page already holds.
  const pdfUrl = `${API_BASE}/client/approve/${encodeURIComponent(slug)}/${encodeURIComponent(code)}/pdf`

  /**
   * THE SAME URL, WITH DISPLAY HINTS FOR WHATEVER VIEWER THE CUSTOMER HAPPENS TO HAVE.
   *
   * We emit one `<object>`; everything around the document - toolbars, a thumbnail rail, a black
   * surround, a 36% zoom - belongs to the browser or to an extension. Reported by the owner looking
   * at this page with Adobe's Acrobat extension installed, which replaces Chrome's built-in viewer
   * with a busier one and opened the ticket small, in a split screen, inside a black frame.
   *
   * That is not a bug in this page, but it is a weakness of it: on the screen where somebody signs,
   * "it looks different for everyone" is not a good answer. These are Adobe's PDF Open Parameters,
   * honoured on a best-effort basis by Chrome's viewer and by Acrobat:
   *
   *   view=FitH    fit the page WIDTH, so the document fills the frame instead of floating small
   *   navpanes=0   no thumbnail rail
   *   toolbar=0    no viewer toolbar - this page has its own Open-in-a-new-tab link below
   *   statusbar=0
   *
   * A fragment, so it never reaches our server and cannot affect what is served. A viewer that
   * ignores all of it renders exactly what it renders today, which is why this is safe.
   *
   * The DOWNLOAD link below deliberately keeps the plain URL: hints that suppress a toolbar are
   * unwanted when the point is to hand somebody the file.
   */
  const pdfViewUrl = `${pdfUrl}#view=FitH&navpanes=0&toolbar=0&statusbar=0`

  const getPos = (e) => {
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    const clientX = e.touches ? e.touches[0].clientX : e.clientX
    const clientY = e.touches ? e.touches[0].clientY : e.clientY
    return { x: clientX - rect.left, y: clientY - rect.top }
  }

  const startDraw = (e) => {
    e.preventDefault()
    const ctx = canvasRef.current.getContext('2d')
    const pos = getPos(e)
    ctx.beginPath()
    ctx.moveTo(pos.x, pos.y)
    setIsDrawing(true)
  }

  const draw = (e) => {
    if (!isDrawing) return
    e.preventDefault()
    const ctx = canvasRef.current.getContext('2d')
    const pos = getPos(e)
    ctx.lineTo(pos.x, pos.y)
    ctx.stroke()
    setHasSigned(true)
  }

  const stopDraw = () => setIsDrawing(false)

  const clearSignature = () => {
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    setHasSigned(false)
  }

  const getSignatureData = () => {
    if (!hasSigned) return null
    return canvasRef.current.toDataURL('image/png')
  }

  // THE SENTENCE COMES FROM THE SERVER, and the page echoes back only which one it displayed. A
  // client-supplied consent string is worthless as evidence, because the party who benefits from
  // the wording would be the party supplying it.
  useEffect(() => {
    let cancelled = false
    fetch(`${API_BASE}/client/approve/${slug}/${code}/consent`)
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (cancelled || !data) return
        setConsent(data.consent || null)
        const pre = data.prefill || {}
        // ONLY INTO AN EMPTY BOX. The fetch resolving after somebody has started typing is exactly
        // the race that would replace their own name with an old one — under a signature.
        if (pre.email) setApproverEmail(v => v || pre.email)
        if (pre.name) setApproverName(v => v || pre.name)
        if (pre.title) setApproverTitle(v => v || pre.title)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [slug, code])

  const handleApprove = async () => {
    if (!approverName.trim()) { setError('Please enter your name'); return }
    if (consent && !agreed) { setError('Please confirm the statement above'); return }
    if (!hasSigned) { setError('Please sign below'); return }
    setError(null)
    setSubmitting(true)
    try {
      const res = await fetch(`${API_BASE}/client/approve/${slug}/${code}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          approved: true,
          approved_by: approverName.trim(),
          signature_data: getSignatureData(),
          signer_title: approverTitle.trim() || null,
          signer_email: approverEmail.trim() || null,
          // Which sentence was shown, never the sentence itself.
          consent_key: consent?.consent_key || null,
          consent_version: consent?.consent_version || null,
          consent_locale: consent?.consent_locale || 'en',
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail || 'Approval failed')
      }
      setResult('approved')
    } catch (e) {
      setError(e.message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleReject = async () => {
    if (!approverName.trim()) { setError('Please enter your name'); return }
    if (!rejectionReason.trim()) { setError('Please provide a rejection reason'); return }
    setError(null)
    setSubmitting(true)
    try {
      const res = await fetch(`${API_BASE}/client/approve/${slug}/${code}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          approved: false,
          approved_by: approverName.trim(),
          rejection_reason: rejectionReason.trim(),
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail || 'Rejection failed')
      }
      setResult('rejected')
    } catch (e) {
      setError(e.message)
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Clock className="w-8 h-8 text-gray-400 mx-auto mb-3 animate-pulse" />
          <p className="text-gray-500">Loading field ticket...</p>
        </div>
      </div>
    )
  }

  if (error && !ticket) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Invalid Link</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    )
  }

  if (result) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md bg-white rounded-2xl shadow-lg p-8">
          {result === 'approved' ? (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Approved!</h2>
              <p className="text-gray-600">Field ticket <strong>{ticket.ticket_number}</strong> has been approved. Thank you.</p>
            </>
          ) : (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Rejected</h2>
              <p className="text-gray-600">Field ticket <strong>{ticket.ticket_number}</strong> has been rejected. {`${brand.name} will be notified.`}</p>
            </>
          )}
        </div>
      </div>
    )
  }

  // Already processed
  if (ticket.status !== 'pending_approval') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md bg-white rounded-2xl shadow-lg p-8">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Already Processed</h2>
          <p className="text-gray-600">This field ticket has already been <strong>{ticket.status.replace('_', ' ')}</strong>.</p>
        </div>
      </div>
    )
  }


  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* ── Document Container (mirrors the PDF) ─────────────── */}
        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          <div className="px-8 pt-8 pb-6">

            {/* -- THE DOCUMENT ------------------------------------------------------------------
                THE SIGNER SEES THE ACTUAL PDF, not a second rendering of it.

                This page used to draw its own HTML version of the ticket. That is a SECOND
                implementation of a document the backend already renders, and the two drifted, as
                two implementations of one document always do. Reported from production on
                2026-08-20, comparing what a customer signs against the PDF that is stored: no
                company logo, the Job # missing, the project name truncated, no sign that the
                contractor had already signed -- and the `Total` and `Per Diem` columns merged under
                one heading reading "Total Per Diem", so an HOURS figure was shown to the signer as
                a per-diem amount.

                THE OLD MARKUP IS NOT KEPT AS A FALLBACK. It was not merely unstyled, it stated
                something untrue about the numbers, and a fallback that misrepresents a money
                document is worse than no fallback.

                `object` rather than `iframe` because it degrades honestly: a browser with no PDF
                viewer renders the children below instead of the blank grey rectangle an iframe
                leaves behind. */}
            <object
              data={pdfViewUrl}
              type="application/pdf"
              className="w-full bg-gray-50 rounded"
              style={{ height: '75vh' }}
              aria-label={`Field ticket ${ticket.ticket_number}`}
            >
              <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                <p className="text-sm text-gray-700">
                  Your browser can&rsquo;t display the ticket here.
                </p>
                <a href={pdfUrl} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1.5 rounded-md bg-bfs-maroon px-4 py-2 text-sm font-medium text-white">
                  Open the ticket
                </a>
              </div>
            </object>

            {/* A plain anchor, not a scripted download: it works when the framed viewer does not,
                and on a phone it hands the file to whatever the person reads PDFs with. */}
            <div className="mt-3 text-center">
              <a href={pdfUrl} target="_blank" rel="noreferrer"
                 className="text-sm text-bfs-maroon underline underline-offset-2">
                Open the ticket in a new tab
              </a>
            </div>

            {/* ── Signature / Approval Section ───────────────────── */}
            <div className="mt-8 border-t-2 border-bfs-maroon pt-4">
              <p className="text-center text-xs text-gray-500 italic mb-1">
                By signing below, the undersigned authorises the work described above
                and confirms the hours and descriptions are accurate.
              </p>
              <p className="text-center text-xs text-gray-800 font-bold mb-5">
                * Sales Tax is not included
              </p>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg flex items-center gap-2 mb-4 text-sm">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-800 mb-1">Client Representative:</label>
                  {/* EVERYTHING HERE IS EDITABLE, including anything we pre-filled. A pre-filled
                      box is a convenience; a signature under a name the signer did not type is us
                      signing for them. What is recorded is what they submit. */}
                  <input value={approverName} onChange={e => setApproverName(e.target.value)}
                    className="w-full border border-gray-300 rounded px-4 py-2 text-sm" placeholder="Enter your full name" />
                </div>

                {!isRejecting && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-bold text-gray-800 mb-1">Your title:</label>
                        <input value={approverTitle} onChange={e => setApproverTitle(e.target.value)}
                          className="w-full border border-gray-300 rounded px-4 py-2 text-sm"
                          placeholder="Optional" autoComplete="organization-title" />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-800 mb-1">Your email:</label>
                        <input value={approverEmail} onChange={e => setApproverEmail(e.target.value)}
                          type="email" autoComplete="email"
                          className="w-full border border-gray-300 rounded px-4 py-2 text-sm"
                          placeholder="Optional" />
                        <p className="text-xs text-gray-500 mt-1">
                          So we can send you a copy, and so we know you next time.
                        </p>
                      </div>
                    </div>

                    {/* WHAT THEY ARE AGREEING TO, in the server's words. Field tickets were signed
                        for months with nothing recorded here at all — the certificate could say who
                        signed and had to stay silent about what was agreed, on the document a T&M
                        invoice is built from. */}
                    {consent?.text && (
                      <label className="flex items-start gap-2 rounded border border-gray-300 bg-gray-50 p-3 text-sm text-gray-800">
                        <input type="checkbox" checked={agreed}
                          onChange={e => setAgreed(e.target.checked)}
                          className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>{consent.text}</span>
                      </label>
                    )}

                    <div>
                      <label className="block text-sm font-bold text-gray-800 mb-1">Signature:</label>
                      <div className="border-2 border-gray-300 rounded bg-white relative">
                        <canvas ref={canvasRef}
                          className="w-full cursor-crosshair touch-none"
                          style={{ height: '120px' }}
                          onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
                          onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
                        />
                        {!hasSigned && (
                          <p className="absolute inset-0 flex items-center justify-center text-gray-300 pointer-events-none text-sm">
                            Sign here
                          </p>
                        )}
                      </div>
                      <button onClick={clearSignature}
                        className="text-xs text-bfs-maroon hover:text-bfs-maroon-light mt-1">Clear signature</button>
                    </div>
                    <button onClick={handleApprove} disabled={submitting}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white rounded font-medium text-lg">
                      <CheckCircle className="w-5 h-5" /> {submitting ? 'Submitting...' : 'Approve Field Ticket'}
                    </button>
                  </>
                )}

                {isRejecting && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-bold text-gray-800 mb-1">Reason for Rejection:</label>
                      <textarea value={rejectionReason} onChange={e => setRejectionReason(e.target.value)}
                        className="w-full border border-gray-300 rounded px-4 py-2 text-sm" rows={3}
                        placeholder="Explain why you are rejecting this field ticket..." />
                    </div>
                    <button onClick={handleReject} disabled={submitting}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white rounded font-medium">
                      <XCircle className="w-5 h-5" /> {submitting ? 'Submitting...' : 'Confirm Rejection'}
                    </button>
                    <button onClick={() => setIsRejecting(false)}
                      className="w-full text-gray-500 hover:text-gray-700 text-sm">Cancel</button>
                  </div>
                )}

                {!isRejecting && (
                  <button onClick={() => setIsRejecting(true)}
                    className="w-full text-red-500 hover:text-red-700 text-xs font-medium py-2">
                    I want to reject this ticket
                  </button>
                )}
              </div>
            </div>

            {/* ── Footer ─────────────────────────────────────────── */}
            <div className="mt-6 border-t border-gray-300 pt-2">
              <p className="text-center text-[10px] text-gray-400">
                {ticket.ticket_number}
              </p>
            </div>

          </div>
        </div>
      </div>
    </div>
  )
}

